/** Top-left corner of a floating panel, in viewport pixels. */
export type NxFloatingPanelPosition = { x: number; y: number };

/** Where a panel opens when neither the caller nor the storage knows a position. */
export type NxFloatingPanelPlacement = 'bottom-right' | 'top-right' | 'bottom-left' | 'center';

/** Pixels of the panel that must stay inside the viewport horizontally. */
export const FLOATING_PANEL_EDGE = 48;
/** Distance to the viewport edge for the initial placement. */
export const FLOATING_PANEL_MARGIN = 24;

/**
 * Keep a panel reachable: at least `edge` pixels stay inside the viewport
 * horizontally, and its header never leaves the top or bottom (so it can
 * always be grabbed again).
 */
export function clampFloatingPosition(
  position: NxFloatingPanelPosition,
  size: { width: number; header: number },
  viewport: { width: number; height: number },
  edge = FLOATING_PANEL_EDGE
): NxFloatingPanelPosition {
  const minX = edge - size.width;
  const maxX = viewport.width - edge;
  const maxY = viewport.height - size.header;
  return {
    x: Math.round(Math.min(Math.max(position.x, minX), Math.max(minX, maxX))),
    y: Math.round(Math.min(Math.max(position.y, 0), Math.max(0, maxY))),
  };
}

/** Distance to the viewport edge: one number for both axes, or one per axis. */
export type NxFloatingPanelMargin = number | { x: number; y: number };

export function initialFloatingPosition(
  placement: NxFloatingPanelPlacement,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  margin: NxFloatingPanelMargin = FLOATING_PANEL_MARGIN
): NxFloatingPanelPosition {
  const gap = typeof margin === 'number' ? { x: margin, y: margin } : margin;
  // A panel as wide as the window starts at its edge, not beyond it.
  const right = Math.max(0, viewport.width - size.width - gap.x);
  const bottom = Math.max(0, viewport.height - size.height - gap.y);
  switch (placement) {
    case 'top-right':
      return { x: right, y: gap.y };
    case 'bottom-left':
      return { x: gap.x, y: bottom };
    case 'center':
      return { x: (viewport.width - size.width) / 2, y: (viewport.height - size.height) / 2 };
    default:
      return { x: right, y: bottom };
  }
}

/** A remembered position as the user's settings keep it, or `null` when it is not one. */
export function storedFloatingPosition(value: unknown): NxFloatingPanelPosition | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Partial<Record<'x' | 'y', unknown>>;
  return typeof x === 'number' && Number.isFinite(x) && typeof y === 'number' && Number.isFinite(y)
    ? { x, y }
    : null;
}
